import { DataGridPremium, GridColumnMenu, GridColumnMenuProps, GridCsvExportOptions, GridEventListener, GridRowSpacingParams, useGridApiRef } from '@mui/x-data-grid-premium'
import { useCallback, useContext, useEffect, useState } from 'react'
import parse from 'html-react-parser'
import { Taxon } from './Taxonomy.service'
import { ExpandState } from './Taxonomy'
import { highlightKeyword } from '../../../../components/SearchText'
import { SampleDataContext } from '../../../../context/CustomContext'

const GRID_BORDER_STYLE = '2px solid rgba(0, 0, 0, 0.12)'
const HEADER_ROW_BACKGROUND_GRADIENT = 'linear-gradient(rgba(240, 240, 255, 0.35), rgba(240, 240, 255, 0.1))'

export interface RankGridProps {
    taxonomicProfileId: string
    taxonomicRank: Taxon[]
    expandState: ExpandState
    isExpanded: boolean
    searchTerm: string
    loading: boolean
    setIsExpanded: (isExpanded: boolean) => void
}

export function RankGrid(props: RankGridProps) {
    const { taxonomicProfileId, taxonomicRank, expandState, isExpanded, searchTerm, loading, setIsExpanded } = props
    const { triggerExport, setTriggerExport } = useContext(SampleDataContext)

    const apiRef = useGridApiRef()

    const initialState = initialStateBuilder(INITIAL_COLUMNS_VISIBILITY)
    const builtColumns = buildColumns(searchTerm)

    useEffect(() => {
        if (expandState !== ExpandState.neutral) {
            setIsExpanded(expandState === ExpandState.expand)
        }
    }, [expandState])

    useEffect(() => {
        if (triggerExport && taxonomicProfileId) {
            const options: GridCsvExportOptions = {
                fileName: `TaxonomicRanks-${taxonomicProfileId}`,
                fields: ['rank', 'name', 'abundance', 'readCount', 'taxonomy'],
            }
            apiRef.current.exportDataAsCsv(options)
            setTriggerExport(false)
        }
    }, [apiRef, triggerExport, setTriggerExport, taxonomicProfileId])

    const getRowSpacing = useCallback((params: GridRowSpacingParams) => {
        const isGroupRow = (params.id as string).startsWith('auto-generated-row-rank')
        return {
            top: isGroupRow ? 20 : 0,
            bottom: 0,
        }
    }, [])

    const onRowClick: GridEventListener<'rowClick'> = (params) => {
        if ((params.id as string).startsWith('auto-generated')) {
            const rowNode = apiRef.current.getRowNode(params.id)
            if (rowNode && rowNode.type === 'group') {
                apiRef.current.setRowChildrenExpansion(params.id, !rowNode.childrenExpanded)
            }
        }
    }

    function CustomColumnMenu(props: GridColumnMenuProps) {
        return (
            <GridColumnMenu
                {...props}
                slots={{
                    columnMenuGroupingItem: null,
                    columnMenuPinningItem: null,
                    columnMenuColumnsItem: null,
                }}
            />
        )
    }

    return (
        <DataGridPremium
            getRowId={(r) => r.id}
            apiRef={apiRef}
            loading={loading}
            autoHeight
            pagination
            pageSizeOptions={[50, 100]}
            sortingOrder={['asc', 'desc']}
            rows={taxonomicRank}
            columns={builtColumns}
            initialState={initialState}
            isGroupExpandedByDefault={() => {
                return isExpanded
            }}
            disableRowSelectionOnClick
            getRowClassName={(params) => `level-${params.row.level}`}
            getRowSpacing={getRowSpacing}
            onRowClick={onRowClick}
            groupingColDef={{
                availableAggregationFunctions: [],
                headerName: 'Taxon name',
                leafField: 'name',
                flex: 1,
                minWidth: 350,
            }}
            slots={{
                noRowsOverlay: () => <NoDataOverlay />,
                columnMenu: CustomColumnMenu,
            }}
            sx={{
                '--DataGrid-cellOffsetMultiplier': 2,
                '.MuiDataGrid-cell:not(:has(.MuiDataGrid-groupingCriteriaCell))': {
                    paddingLeft: '10px',
                },
                '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus': {
                    outline: 'none',
                },
                '.MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 'bold',
                },
                '.keyword': {
                    backgroundColor: '#FFEBFF',
                    padding: '8px 0',
                },
                '.MuiDataGrid-row:has([data-testid="ExpandMoreIcon"])': {
                    backgroundImage: HEADER_ROW_BACKGROUND_GRADIENT,
                    borderTop: GRID_BORDER_STYLE,
                    cursor: 'pointer',
                    borderRadius: '5px 5px 0 0',
                },
                '.MuiDataGrid-row:has([data-testid="KeyboardArrowRightIcon"])': {
                    backgroundImage: HEADER_ROW_BACKGROUND_GRADIENT,
                    borderTop: GRID_BORDER_STYLE,
                    borderBottom: GRID_BORDER_STYLE,
                    cursor: 'pointer',
                    borderRadius: '5px',
                    '& .MuiDataGrid-cell': {
                        borderBottom: 'none',
                    },
                },
                '.MuiDataGrid-columnHeaders': {
                    backgroundImage: HEADER_ROW_BACKGROUND_GRADIENT,
                },
                '& .project-name': {
                    fontWeight: 'bold',
                },
                '.MuiDataGrid-row': {
                    width: 'calc(100% - 4px)',
                    borderLeft: GRID_BORDER_STYLE,
                    borderRight: GRID_BORDER_STYLE,
                },
                '.level-leaf': {
                    borderBottom: GRID_BORDER_STYLE,
                    borderRadius: '0 0 5px 5px',
                    '& .MuiDataGrid-cell': {
                        borderBottom: 'none',
                    },
                },
                '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
                    display: 'none',
                },
                border: 'none',
                marginTop: '15px',
                width: '100%',
            }}
        />
    )
}

const buildColumns = (searchTerm: string) => {
    const columns = [
        {
            field: 'rank',
            headerName: 'Taxon name',
            availableAggregationFunctions: [],
            renderCell: (params) => renderGroup(params, searchTerm),
        },
        {
            field: 'name',
            headerName: 'Name',
            renderCell: (params) => renderHTML(params.row.name, searchTerm, 'name'),
        },
        {
            field: 'abundance',
            headerName: 'Abundance (%)',
            availableAggregationFunctions: ['avg'],
            width: 200,
            valueFormatter: ({ value }) => renderAbundance(value),
        },
        {
            field: 'readCount',
            headerName: 'No. reads',
            availableAggregationFunctions: ['sum'],
            width: 200,
            valueFormatter: ({ value }) => renderReadCount(value),
        },
        {
            field: 'taxonomy',
            headerName: 'Taxonomy',
            availableAggregationFunctions: [],
            flex: 3,
            renderCell: (params) => renderHTML(params.row.taxonomy, searchTerm),
        },
    ]
    return columns
}

const renderAbundance = (value: any) => {
    if (!value) {
        return null
    }
    return Math.ceil(value * 1000) / 1000
}

const renderReadCount = (value: any) => {
    if (!value) {
        return null
    }
    return value.toLocaleString('en-US')
}

const renderGroup = (params: any, searchTerm: string) => {
    return renderHTML(params.value, searchTerm)
}

const renderHTML = (value: string, searchTerm: string, classes?: string) => {
    if (!value) {
        return null
    }

    const htmlValue = highlightKeyword(value, searchTerm)
    return <div className={classes}>{parse(htmlValue)}</div>
}

const initialStateBuilder = (columnVisibilityModel: any) => {
    return {
        pagination: {
            paginationModel: { pageSize: 50, page: 0 },
        },
        rowGrouping: {
            model: ['rank'],
        },
        columns: {
            columnVisibilityModel: columnVisibilityModel,
        },
    }
}

const INITIAL_COLUMNS_VISIBILITY = {
    rank: false,
    name: false,
}

const NoDataOverlay = () => {
    const [show, setShow] = useState(false)

    useEffect(() => {
        const timeout = setTimeout(() => setShow(true), 300)
        return () => clearTimeout(timeout)
    }, [])

    if (!show) return null
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
            }}
        >
            <p>No data found</p>
        </div>
    )
}
